// Décide si l'onglet peut se recharger MAINTENANT sur la nouvelle version.
// Appelée aux trois moments sûrs de la spec (changement de module, retour sur
// l'onglet, inactivité). Ne lève jamais : au pire, elle ne recharge pas.
import { isStorageEphemeral } from "@/lib/storageFallback";
import { getAppUpdateState } from "./store";
import { hasUnsavedChanges } from "./unsavedChanges";

export type ReloadReason = "navigation" | "visible" | "idle";

export interface ReloadDeps {
  location: Pick<Location, "pathname" | "reload" | "assign">;
  sessionStorage: Storage;
  document: Document;
  now: () => number;
  storageEphemeral: () => boolean;
  isOnline: () => boolean;
  hasUnsaved: () => boolean;
}

export const RELOAD_GUARD_KEY = "appUpdate_reload";
export const RELOAD_GUARD_MS = 10 * 60 * 1000;

/**
 * Liste BLANCHE des espaces connectés. Réservation, paiement, confirmation,
 * authentification : exclus, le client y perdrait ses choix en mémoire.
 */
export function isConnectedRoute(pathname: string): boolean {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return path.startsWith("/owner/") || path.startsWith("/admin/") || path === "/client/history";
}

function hasOpenDialog(doc: Document): boolean {
  return doc.querySelector('[role="dialog"], [role="alertdialog"]') !== null;
}

function isEditingField(doc: Document): boolean {
  const el = doc.activeElement as HTMLElement | null;
  if (!el || el === doc.body) return false;
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  const editable = el.getAttribute("contenteditable");
  return el.isContentEditable === true || editable === "" || editable === "true";
}

function defaultDeps(): ReloadDeps {
  return {
    location: window.location,
    sessionStorage: window.sessionStorage,
    document,
    now: Date.now,
    storageEphemeral: () => isStorageEphemeral("sessionStorage"),
    isOnline: () => navigator.onLine !== false,
    hasUnsaved: hasUnsavedChanges,
  };
}

/** Adresse du tableau de bord propriétaire ouverte sur un module (lue par l'effet `?view=`). */
export function ownerViewUrl(view: string): string {
  return `/owner/dashboard?view=${encodeURIComponent(view)}`;
}

export function reloadIfUpdatePending(reason: ReloadReason, targetUrl?: string, deps?: ReloadDeps): boolean {
  try {
    const { status, remoteBuildId } = getAppUpdateState();
    if (status === "idle" || !remoteBuildId) return false;

    const d = deps ?? defaultDeps();
    if (!isConnectedRoute(d.location.pathname)) return false;
    // Hors ligne, la recharge afficherait la page d'erreur du navigateur à la
    // place de l'app : jamais, quel que soit le moment.
    if (!d.isOnline()) return false;
    // Changement de module : la personne quitte déjà son écran. Retour d'onglet
    // et inactivité : elle peut avoir une saisie en cours, on ne touche à rien.
    // Même règle pour une fiche modifiée en ligne mais pas encore enregistrée.
    if (reason !== "navigation" && (hasOpenDialog(d.document) || isEditingField(d.document) || d.hasUnsaved())) {
      return false;
    }
    // Stockage en mémoire seulement : la marque anti-boucle ne survivrait pas
    // à la recharge qu'elle doit garder (même garde que lazyWithRetry).
    if (d.storageEphemeral()) return false;

    const raw = d.sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (raw) {
      try {
        const guard = JSON.parse(raw) as { buildId?: unknown; at?: unknown };
        if (guard.buildId === remoteBuildId && typeof guard.at === "number" && d.now() - guard.at < RELOAD_GUARD_MS) {
          return false;
        }
      } catch {
        // Marque illisible : on l'écrase ci dessous.
      }
    }
    d.sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({ buildId: remoteBuildId, at: d.now() }));

    if (targetUrl) d.location.assign(targetUrl);
    else d.location.reload();
    return true;
  } catch {
    return false;
  }
}
